// frontend/src/pages/QuizPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Play, Trophy, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import apiService from "../services/apiService";
import { getQuizTranslation } from "../utils/quizTranslations";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ContractBadge from "../components/common/ContractBadge";
import QuizTypeBadge from "../components/quiz/QuizTypeBadge";
import QuizTaker from "../components/quiz/QuizTaker";

const QuizPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const { hasAccess, isAuthenticated } = useAuth();

  const [quiz, setQuiz] = useState(null);
  const [quizAttempt, setQuizAttempt] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadQuiz();
  }, [slug, language]);

  const loadQuiz = async () => {
    try {
      const response = await apiService.getQuiz(slug, language);
      if (response.success) {
        setQuiz(response.data);
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error("Error loading quiz:", error);
      setError(getQuizTranslation("messages.errorLoadingQuiz", language));
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = async () => {
    if (!isAuthenticated) {
      navigate(`/login?returnUrl=${encodeURIComponent(location.pathname)}`);
      return;
    }

    if (!quiz.hasAccess) {
      alert(getQuizTranslation("messages.accessDenied", language));
      return;
    }

    setStarting(true);

    try {
      const response = await apiService.startQuiz(quiz.quiz_id, language);
      if (response.success) {
        setQuizAttempt(response.data);
      } else {
        alert(response.message);
      }
    } catch (error) {
      console.error("Error starting quiz:", error);
      alert(getQuizTranslation("messages.errorLoadingQuiz", language));
    } finally {
      setStarting(false);
    }
  };

  const handleQuizComplete = (resultData) => {
    setResults(resultData);
    setQuizAttempt(null);
  };

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={handleBack}
          className="flex items-center text-[#00A3E0] hover:text-[#002855] mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {getQuizTranslation("results.backToQuiz", language)}
        </button>
        <div className="text-center text-red-600">{error}</div>
      </div>
    );
  }

  // Show quiz attempt if active
  if (quizAttempt) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <QuizTaker quizData={quizAttempt} onComplete={handleQuizComplete} />
      </main>
    );
  }

  // Show results if completed
  if (results) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={handleBack}
          className="flex items-center text-[#00A3E0] hover:text-[#002855] mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {getQuizTranslation("results.backToQuiz", language)}
        </button>

        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            {results.passed ? (
              <>
                <Trophy className="w-20 h-20 text-yellow-500 mx-auto mb-4" />
                <h1 className="text-3xl font-bold text-green-600 mb-2">
                  {getQuizTranslation("results.congratulations", language)}
                </h1>
                <p className="text-xl text-gray-700">
                  {getQuizTranslation("results.passed", language)}
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">😔</span>
                </div>
                <h1 className="text-3xl font-bold text-gray-700 mb-2">
                  {getQuizTranslation("results.failed", language)}
                </h1>
                <p className="text-xl text-gray-600">
                  {getQuizTranslation("results.tryAgain", language)}
                </p>
              </>
            )}
          </div>

          {/* Score Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                {getQuizTranslation("ui.yourScore", language)}
              </p>
              <p className="text-3xl font-bold text-[#EE005F]">
                {results.score}%
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                {getQuizTranslation("ui.points", language)}
              </p>
              <p className="text-3xl font-bold text-[#00A3E0]">
                {results.points_earned}/{results.points_possible}
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                {getQuizTranslation("results.timeTaken", language)}
              </p>
              <p className="text-3xl font-bold text-[#04A777]">
                {Math.floor(results.time_taken / 60)}:
                {(results.time_taken % 60).toString().padStart(2, "0")}
              </p>
            </div>
          </div>

          {/* Badge Earned */}
          {results.badge_earned && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6 mb-8 border-2 border-yellow-300">
              <div className="flex items-center gap-4">
                <Sparkles className="w-12 h-12 text-yellow-600" />
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800 mb-1">
                    {getQuizTranslation("results.badgeEarned", language)}
                  </h3>
                  <p className="text-gray-700">{results.badge_earned.name}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-center gap-4">
            <button
              onClick={handleBack}
              className="px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {getQuizTranslation("results.backToQuiz", language)}
            </button>
            {!results.passed && (
              <button
                onClick={handleStartQuiz}
                className="px-6 py-3 rounded-lg bg-[#EE005F] text-white hover:bg-[#c7004d] transition-colors font-medium"
              >
                {getQuizTranslation("ui.retakeQuiz", language)}
              </button>
            )}
          </div>
        </div>
      </main>
    );
  }

  // Show quiz intro/start screen
  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button
        onClick={handleBack}
        className="flex items-center text-[#00A3E0] hover:text-[#002855] mb-6 transition-colors"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        {getQuizTranslation("results.backToQuiz", language)}
      </button>

      <div className="bg-white rounded-lg shadow-xl overflow-hidden">
        {/* Header Image */}
        <div className="relative h-64 bg-gradient-to-br from-[#EE005F] to-[#00A3E0]">
          <img
            src={quiz.imageUrl}
            alt={t(quiz.title)}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#00A3E0]/60 to-[#002855]/60" />
          <div className="absolute bottom-4 left-4 right-4">
            <h1 className="text-3xl font-bold text-white mb-2">{t(quiz.title)}</h1>
            <div className="flex items-center gap-2">
              <QuizTypeBadge quizType={quiz.quiz_type} />
              <ContractBadge contract={quiz.contract} />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {quiz.description && (
            <p className="text-gray-700 mb-6">{t(quiz.description)}</p>
          )}

          {/* Quiz Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                {getQuizTranslation("ui.questions", language)}
              </p>
              <p className="text-2xl font-bold text-[#002855]">
                {quiz.questionCount || 0}
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                {getQuizTranslation("ui.passingScore", language)}
              </p>
              <p className="text-2xl font-bold text-[#002855]">
                {quiz.passing_score}%
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                {getQuizTranslation("ui.timeLimit", language)}
              </p>
              <p className="text-2xl font-bold text-[#002855]">
                {quiz.time_limit
                  ? `${Math.floor(quiz.time_limit / 60)} min`
                  : getQuizTranslation("ui.unlimited", language)}
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                {getQuizTranslation("ui.attemptsRemaining", language)}
              </p>
              <p className="text-2xl font-bold text-[#002855]">
                {quiz.attempts_allowed || getQuizTranslation("ui.unlimited", language)}
              </p>
            </div>
          </div>

          {/* Start Button */}
          {quiz.hasAccess ? (
            <button
              onClick={handleStartQuiz}
              disabled={starting}
              className="w-full py-4 rounded-lg bg-[#00A3E0] text-white hover:bg-[#0082b3] transition-colors font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {starting ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-3 border-white border-t-transparent"></div>
                  {getQuizTranslation("messages.loadingQuiz", language)}
                </>
              ) : (
                <>
                  <Play className="w-6 h-6" />
                  {getQuizTranslation("ui.startQuiz", language)}
                </>
              )}
            </button>
          ) : (
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <p className="text-red-600 font-medium mb-4">
                {getQuizTranslation("messages.accessDenied", language)}
              </p>
              {!isAuthenticated && (
                <button
                  onClick={() => navigate(`/login?returnUrl=${encodeURIComponent(location.pathname)}`)}
                  className="px-6 py-3 rounded-lg bg-[#00A3E0] text-white hover:bg-[#0082b3] transition-colors font-medium"
                >
                  {getQuizTranslation("messages.loginRequired", language)}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default QuizPage;
